// Parsers de la Carpeta Tributaria (SII) y del Certificado de Deuda (TGR).
// Entrada: arreglo de páginas; cada página es un arreglo de líneas con celdas separadas por " | ".

export const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const MES_ABR = { ene: 1, feb: 2, mar: 3, abr: 4, may: 5, jun: 6, jul: 7, ago: 8, sep: 9, set: 9, oct: 10, nov: 11, dic: 12 };

export const num = (s) => {
  if (s === undefined || s === null) return null;
  s = String(s).trim();
  if (!/^-?[\d.]+(,\d+)?$/.test(s)) return null;
  return Number(s.replace(/\./g, "").replace(",", "."));
};

/** Número de la Carpeta Tributaria: montos chilenos (1.234 / 1.234,56) y tasas anglosajonas (0.125, 12.5). */
export const numCarpeta = (s) => {
  if (s === undefined || s === null) return null;
  const raw = String(s).trim();
  // 0.125 (PPM 0,125%), 0.1, 0.01 → decimal; no son miles (no empiezan en 0.)
  if (/^-?0\.\d+$/.test(raw)) return parseFloat(raw);
  // 12.5, 1.5 → decimal anglosajón de 1–2 decimales (no confundir con 1.234 = mil doscientos…)
  if (/^-?\d+\.\d{1,2}$/.test(raw) && raw.split(".")[1].length <= 2) return parseFloat(raw);
  return num(raw);
};
const cells = (l) => l.split(" | ").map((c) => c.trim());
const isRutCell = (s) => /^\d{1,2}\.?\d{3}\.?\d{3}-[\dkK]$/.test(s || "") || /^\d{6,9}-[\dkK]$/.test(s || "");
/** Apellido u otro fragmento de nombre solo en su línea (pdf.js deja el apellido materno debajo). */
const isNameOnlyLine = (l) => {
  const t = (l || "").trim();
  if (!t || t.includes(" | ") || /\d/.test(t)) return false;
  return /^[A-ZÁÉÍÓÚÑÜ][A-ZÁÉÍÓÚÑÜa-záéíóúñü\s.'’\-]*$/.test(t);
};
const rutIndex = (c) => c.findIndex(isRutCell);

export const parseDMY = (s) => {
  const m = /(\d{2})[/-](\d{2})[/-](\d{4})/.exec(s || "");
  return m ? new Date(+m[3], +m[2] - 1, +m[1]) : null;
};
export const parseDMonY = (s) => {
  const m = /(\d{2})-([A-Za-zé]{3})-(\d{4})/.exec(s || "");
  return m ? new Date(+m[3], (MES_ABR[m[2].toLowerCase()] || 1) - 1, +m[1]) : null;
};

export function detectType(pages) {
  const t = pages.flat().join("\n");
  if (/CARPETA TRIBUTARIA/i.test(t)) return "carpeta";
  if (/Certificado de Deuda/i.test(t) || /Servicio de Tesorer/i.test(t)) return "tgr";
  return null;
}

// ---------------- CARPETA TRIBUTARIA ----------------
export function parseCarpeta(pages) {
  const lines = pages.flat();
  const field = (label) => {
    const l = lines.find((x) => x.startsWith(label));
    if (!l) return null;
    return cells(l).slice(1).join(" ").trim() || null;
  };
  const out = {
    tipo: "carpeta",
    razonSocial: field("Nombre del Emisor:"),
    rut: field("RUT del Emisor:"),
    fechaGeneracion: field("Fecha de generación de la Carpeta:"),
    inicioActividades: field("Fecha de Inicio de Actividades:"),
    fechaConstitucion: field("Fecha de Constitución:"),
    clasificacion: field("Clasificación Entidad:"),
    categoria: field("Categoría Tributaria:"),
    regimen: null,
    contabilidad: null,
    domicilio: field("Domicilio:"),
    actividades: [],
    documentos: [],
    anotaciones: [],
    representantes: [],
    socios: [],
    bienesRaices: [],
    bheEmitidas: [],
    bteRecibidas: [],
    f29: [],
    f22: [],
  };
  const ri = lines.findIndex((x) => x.startsWith("Régimen Tributario:"));
  if (ri >= 0) {
    out.regimen = cells(lines[ri]).slice(1).join(" ");
    const nx = lines[ri + 1] || "";
    if (/CONTABILIDAD/i.test(nx)) out.contabilidad = nx.trim();
  }
  for (let i = 0; i < lines.length; i++) {
    const c = cells(lines[i]);
    if (/^\d{6}$/.test(c[0]) && c.length >= 3 && /Categor/i.test(c[2] || "")) {
      let glosa = c[1];
      const nx = lines[i + 1] || "";
      if (nx && !nx.includes(" | ") && !/^\d{6}/.test(nx) && !/:/.test(nx)) glosa += " " + nx.trim();
      out.actividades.push({ codigo: c[0], glosa, categoria: c[2], desde: c[3] });
    }
  }
  const di = lines.findIndex((x) => x.startsWith("Últimos documentos autorizados"));
  if (di >= 0) {
    for (let i = di + 1; i < lines.length; i++) {
      const c = cells(lines[i]);
      if (c.length === 2 && /\d{2}\/\d{2}\/\d{4}/.test(c[1])) out.documentos.push({ tipo: c[0], fecha: c[1] });
      else break;
    }
  }
  const ai = lines.findIndex((x) => x.startsWith("Anotaciones y Observaciones"));
  const ae = lines.findIndex((x, i) => i > ai && x.startsWith("Información proporcionada"));
  if (ai >= 0) {
    const block = lines.slice(ai + 1, ae > ai ? ae : ai + 10).filter((x) => !/^Fecha \| Glosa/.test(x));
    if (!block.some((x) => /No registra/i.test(x))) out.anotaciones = block.filter((x) => x.trim());
  }
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    // Nombres largos: pdf.js deja "NOMBRE | RUT | …" y el apellido materno en la línea siguiente → no cortar ahí.
    if (l.startsWith("Representante(s) Legal(es)")) {
      const pushRep = (c, prefix = "") => {
        const ri = rutIndex(c);
        if (ri < 0) return false;
        const nombre = `${prefix} ${c.slice(0, ri).join(" ")}`.trim();
        out.representantes.push({
          nombre,
          rut: c[ri],
          desde: c[ri + 1] || null,
          actuacion: c.slice(ri + 2).join(" ").trim() || null,
        });
        return true;
      };
      let pending = "";
      const c0 = cells(l).slice(1);
      if (!pushRep(c0)) pending = c0.join(" ").trim();
      for (let j = i + 1; j < lines.length; j++) {
        const lj = lines[j];
        if (isNameOnlyLine(lj)) {
          const last = out.representantes[out.representantes.length - 1];
          if (last) last.nombre = `${last.nombre} ${lj.trim()}`.trim();
          else pending = `${pending} ${lj.trim()}`.trim();
          continue;
        }
        if (/^(Nombre o Razón|Conformación|Participación|Propiedades|Boletas|\(\d\))/i.test(lj) || /^Pág\./.test(lj)) break;
        const cc = cells(lj);
        if (pushRep(cc, pending)) {
          pending = "";
          continue;
        }
        break;
      }
    }
    if (/^Conformaci[oó]n de la sociedad/i.test(l)) {
      const pct = (v) => {
        const n = parseFloat(String(v || "").replace("%", "").trim().replace(",", "."));
        return Number.isFinite(n) ? n : null;
      };
      const pushSoc = (c, prefix = "") => {
        const ri = rutIndex(c);
        if (ri < 0) return false;
        const rest = c.slice(ri + 1);
        const pctCells = rest.filter((x) => /%/.test(x) || /^-?\d+([.,]\d+)?$/.test(x));
        const desde = rest.find((x) => /\d{2}\/\d{2}\/\d{4}/.test(x)) || rest[0] || null;
        out.socios.push({
          nombre: `${prefix} ${c.slice(0, ri).join(" ")}`.trim(),
          rut: c[ri],
          desde,
          capital: pct(pctCells[0]),
          utilidades: pct(pctCells[1] ?? pctCells[0]),
        });
        return true;
      };
      let pending = "";
      const c0 = cells(l).slice(1);
      if (!pushSoc(c0)) pending = c0.join(" ").trim();
      for (let j = i + 1; j < lines.length; j++) {
        const lj = lines[j];
        if (isNameOnlyLine(lj)) {
          const last = out.socios[out.socios.length - 1];
          if (last) last.nombre = `${last.nombre} ${lj.trim()}`.trim();
          else pending = `${pending} ${lj.trim()}`.trim();
          continue;
        }
        if (/^(Nombre o Razón|Participación|Propiedades|Boletas|\(\d\))/i.test(lj) || /^Pág\./.test(lj)) break;
        const cc = cells(lj);
        if (rutIndex(cc) >= 0 && cc.some((x) => /%/.test(x))) {
          pushSoc(cc, pending);
          pending = "";
          continue;
        }
        break;
      }
    }
  }
  const bi = lines.findIndex((x) => x.startsWith("Propiedades y Bienes Ra"));
  if (bi >= 0 && !/No registra/i.test(lines[bi + 1] || "")) {
    for (let j = bi + 1; j < lines.length && !lines[j].startsWith("(3)"); j++) out.bienesRaices.push(lines[j]);
  }
  const tabla = (startRe) => {
    const s = lines.findIndex((x) => startRe.test(x));
    const rows = [];
    if (s < 0) return rows;
    for (let j = s + 2; j < lines.length; j++) {
      const l = lines[j];
      if (/^Pág\./.test(l)) continue;
      if (/No registra/i.test(l) || /^\(6\)/.test(l) || /^Boleta de prestación|^Declaraciones de IVA/.test(l)) break;
      const c = cells(l);
      const nums = c.slice(1).map(num);
      if (c.length >= 3 && nums.every((n) => n !== null)) rows.push({ periodo: c[0], bruto: nums[0], retencion: nums[1], otro: nums[2] ?? null });
    }
    return rows;
  };
  out.bheEmitidas = tabla(/^Boletas de Honorarios electrónicas emitidas/);
  out.bteRecibidas = tabla(/^Boleta de prestación de servicios de terceros/);

  // ---- F29 ----
  const f29Start = lines.findIndex((x) => x.startsWith("Declaraciones de IVA"));
  const f22Start = lines.findIndex((x) => x.startsWith("Declaraciones de Renta"));
  const f29Lines = lines.slice(f29Start, f22Start > 0 ? f22Start : undefined);
  const headRe = new RegExp("^(" + MESES.join("|") + ") (\\d{4}) \\| \\d+\\/\\d+", "i");
  let cur = null;
  const flush = () => {
    if (cur) out.f29.push(finishF29(cur));
  };
  for (const l of f29Lines) {
    const h = headRe.exec(l);
    if (h) {
      flush();
      cur = { mes: MESES.indexOf(h[1].toLowerCase()) + 1, anio: +h[2], lines: [] };
      continue;
    }
    if (cur) cur.lines.push(l);
  }
  flush();

  // ---- F22 ----
  if (f22Start >= 0) {
    let c22 = null;
    for (const l of lines.slice(f22Start)) {
      const h = /^Año Tributario (\d{4})/.exec(l);
      if (h) {
        if (c22) out.f22.push(finishF22(c22));
        c22 = { at: +h[1], lines: [] };
        continue;
      }
      if (c22) c22.lines.push(l);
    }
    if (c22) out.f22.push(finishF22(c22));
  }
  return out;
}

function codesFrom(lines) {
  const codes = {};
  for (const l of lines) {
    const c = cells(l);
    for (let i = 0; i < c.length - 2; i++) {
      if (/^\d{2,3}$/.test(c[i]) && num(c[i + 1]) === null && c[i + 2] !== undefined) {
        const v = numCarpeta(c[i + 2]);
        if (v !== null) {
          codes[String(+c[i])] = v;
          i += 2;
        }
      }
    }
    const t = /TOTAL A PAGAR DENTRO DEL PLAZO LEGAL \| 91 \| ([\d.]+)/.exec(l);
    if (t) codes["91"] = num(t[1]);
    const t2 = /TOTAL A PAGAR CON RECARGO \| 94 \| ([\d.]+)/.exec(l);
    if (t2) codes["94"] = num(t2[1]);
    const t3 = /Más IPC \| 92 \| ([\d.]+)/.exec(l);
    if (t3) codes["92"] = num(t3[1]);
    const t4 = /Más Interes y Multas \| 93 \| ([\d.]+)/.exec(l);
    if (t4) codes["93"] = num(t4[1]);
  }
  return codes;
}

function finishF29(b) {
  const r = { mes: b.mes, anio: b.anio, periodo: b.anio * 100 + b.mes, declarado: false };
  if (b.lines.some((l) => /No se registra declaración/i.test(l))) return r;
  r.declarado = true;
  const folioL = b.lines.find((l) => /FOLIO \| \[07\]/.test(l));
  r.folio = folioL ? cells(folioL).pop() : null;
  r.codes = codesFrom(b.lines);
  const ti = b.lines.findIndex((l) => l.startsWith("Tipo de Declaración"));
  if (ti >= 0) {
    const c = cells(b.lines[ti + 1] || "");
    r.tipoDecl = c[0];
    r.fechaPresentacion = c.find((x) => /\d{2}\/\d{2}\/\d{4}/.test(x)) || null;
    const rest = c.slice(1).filter((x) => !/\d{2}\/\d{2}\/\d{4}/.test(x));
    if (/^\d{6,}$/.test(rest[0] || "")) r.corrigeFolio = rest.shift();
    r.banco = rest[0] || null;
    r.medioPago = rest[1] || null;
  }
  return r;
}

function finishF22(b) {
  const r = { at: b.at, declarado: !b.lines.some((l) => /No existen declaraciones/i.test(l)) };
  if (r.declarado) {
    r.codes = codesFrom(b.lines);
    const f = b.lines.find((l) => /FOLIO/.test(l));
    r.folio = f ? cells(f).pop() : null;
  }
  return r;
}

// ---------------- CERTIFICADO TGR ----------------
export function parseTGR(pages) {
  const lines = pages.flat();
  const all = lines.join("\n");
  const get = (label) => {
    const l = lines.find((x) => x.startsWith(label));
    return l ? cells(l).slice(1).join(" ") : null;
  };
  const out = { tipo: "tgr", nombre: get("NOMBRE"), rut: get("RUT"), morosa: [], noVencida: [], convenio: [], otros: [] };
  const liq = /Liquidada al: (\d{2}-\d{2}-\d{4})/.exec(all);
  out.liquidadaAl = liq ? liq[1] : null;
  const em = /Fecha de Emisión del Certificado: (\d{2}-\d{2}-\d{4})/.exec(all);
  out.emision = em ? em[1] : null;
  const cod = lines.find((l) => /^\d{3}[A-Z]{2}\d{10,}$/.test(l.trim()));
  out.codigo = cod || null;
  out.sinDeuda = /no registra deuda/i.test(all);
  let sec = "otros";
  const rowRe = /^(\d+) \| (\d+) \| (\d+) \| (\d{2}-[A-Za-zé]{3}-\d{4}) \| ([\d.]+) \| ([\d.]+) \| ([\d.]+) \| ([\d.]+) \| ([\d.]+)/;
  for (const l of lines) {
    if (/^Deuda\s*:?\s*Morosa/i.test(l)) sec = "morosa";
    else if (/^Deuda No Vencida/i.test(l)) sec = "noVencida";
    else if (/convenio|196|197/i.test(l) && /^Deuda/i.test(l)) sec = "convenio";
    const m = rowRe.exec(l);
    if (m) {
      out[sec].push({
        formulario: m[1], tipo: m[2], folio: m[3], vencimiento: m[4], venc: parseDMonY(m[4]),
        neta: num(m[5]), reajuste: num(m[6]), interes: num(m[7]), multa: num(m[8]), total: num(m[9]),
      });
    }
  }
  const sum = (a) => a.reduce((s, r) => s + r.total, 0);
  out.totalMorosa = sum(out.morosa);
  out.totalNoVencida = sum(out.noVencida);
  out.totalConvenio = sum(out.convenio);
  return out;
}

// Al guardar en el servidor, las fechas de vencimiento viajan como texto; se reconstruyen al abrir.
export function reviveTGR(T) {
  if (!T) return T;
  const fix = (rows) => (rows || []).map((r) => ({ ...r, venc: parseDMonY(r.vencimiento) }));
  return { ...T, morosa: fix(T.morosa), noVencida: fix(T.noVencida), convenio: fix(T.convenio), otros: fix(T.otros) };
}
